// The stand-in for React's `useActionState`.
//
// The old forms did:
//
//   const [estado, acao] = useActionState(criarMarco, ESTADO_INICIAL);
//   <form action={acao}>
//
// Server Actions are gone, but the SHAPE is worth keeping: it's what lets the
// existing form JSX — including every `{estado.erro && <p role="alert">…}` —
// survive the migration untouched. So this returns the same tuple, built on
// TanStack Query's useMutation, and maps a thrown ApiError onto the same
// `{ erro, ok }` object the actions returned.
//
// It works because the API deliberately returns the same Portuguese sentences
// the Server Actions did.

import { useCallback, useState } from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { INITIAL_FORM_STATE, type FormState } from "@historia/shared/contracts";
import { ApiError } from "./client";

export type { FormState };

interface Options {
  /** Query keys to invalidate on success — the replacement for revalidatePath. */
  invalidate?: QueryKey[];
  /** Runs after a successful call, with whatever the mutation returned. */
  onSuccess?: (result: unknown) => void;
}

/**
 * Returns `[estado, acao, aGuardar]`:
 *   estado    — { erro, ok }, the same object the Server Actions returned
 *   acao      — call it with the input; never throws, it sets `estado` instead
 *   aGuardar  — true while in flight (was `useFormStatus().pending`)
 */
export function useAccao<TInput, TResult = unknown>(
  fn: (input: TInput) => Promise<TResult>,
  options: Options = {},
): [FormState, (input: TInput) => Promise<FormState>, boolean] {
  const [estado, setEstado] = useState<FormState>(INITIAL_FORM_STATE);
  const queryClient = useQueryClient();
  const { invalidate, onSuccess } = options;

  const mutation = useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      for (const key of invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      onSuccess?.(result);
    },
  });

  const { mutateAsync } = mutation;

  const acao = useCallback(
    async (input: TInput): Promise<FormState> => {
      try {
        await mutateAsync(input);
        const ok: FormState = { erro: null, ok: true };
        setEstado(ok);
        return ok;
      } catch (error) {
        // The API's message is already the Portuguese sentence meant for a
        // person, so it goes straight into the form's error slot.
        const falha: FormState = {
          erro:
            error instanceof ApiError
              ? error.message
              : "Algo correu mal. Tenta novamente.",
          ok: false,
        };
        setEstado(falha);
        return falha;
      }
    },
    [mutateAsync],
  );

  return [estado, acao, mutation.isPending];
}

/** Reads and trims a text field from a FormData — the old `texto()` helper. */
export function texto(formData: FormData, chave: string): string {
  return String(formData.get(chave) ?? "").trim();
}
